import { type FormEvent, useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { medicineQueryKeys, medicinesApi } from "../../../shared/api/medicines";
import type {
  MealTiming,
  MedicineClassification,
  MedicineCreateRequest,
  MedicineResponse,
  MedicineUpdateRequest,
  VatCategory,
} from "../../../shared/types/medicine";
import {
  mealTimingLabel,
  medicineClassificationLabel,
  vatCategoryLabel,
} from "../../../shared/format";
import { apiError } from "../../../shared/api/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

interface Props {
  open: boolean;
  mode: "create" | "edit";
  initial?: MedicineResponse | null;
  onClose: () => void;
  onSaved: (m: MedicineResponse) => void;
}

const EMPTY: MedicineCreateRequest = {
  internalCode: "",
  name: "",
  activeIngredient: "",
  dosageForm: "",
  strength: "",
  unit: "viên",
  classification: "DRUG",
  categoryId: null,
  manufacturer: "",
  routeOfAdministration: "ORAL",
  mealTiming: null,
  dosageInstructions: "",
  sellPriceVnd: 0,
  costPriceVnd: 0,
  vatCategory: "KCT",
  keywords: "",
  imageUrl: "",
  controlled: false,
  visible: true,
  nationalDrugCode: "",
  barcode: "",
};

// Native <select> styled to match the shadcn input primitives.
const selectClass =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

function fromMedicine(m: MedicineResponse): MedicineCreateRequest {
  const national = m.codeMappings.find((c) => c.sourceSystem === "NATIONAL_DRUG_DB")?.sourceCode;
  const gtin = m.codeMappings.find((c) => c.sourceSystem === "GTIN")?.sourceCode;
  return {
    internalCode: m.internalCode,
    name: m.name,
    activeIngredient: m.activeIngredient,
    dosageForm: m.dosageForm,
    strength: m.strength,
    unit: m.unit,
    classification: m.classification,
    categoryId: m.categoryId,
    manufacturer: m.manufacturer,
    routeOfAdministration: m.routeOfAdministration,
    mealTiming: m.mealTiming,
    dosageInstructions: m.dosageInstructions,
    sellPriceVnd: m.sellPriceVnd,
    costPriceVnd: m.costPriceVnd,
    vatCategory: m.vatCategory,
    keywords: m.keywords,
    imageUrl: m.imageUrl,
    controlled: m.controlled,
    visible: m.visible,
    nationalDrugCode: national ?? "",
    barcode: gtin ?? "",
  };
}

export function MedicineFormModal({ open, mode, initial, onClose, onSaved }: Props) {
  const [data, setData] = useState<MedicineCreateRequest>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const routes = useQuery({
    queryKey: medicineQueryKeys.routes(),
    queryFn: () => medicinesApi.listRoutes(),
    enabled: open,
  });
  const categories = useQuery({
    queryKey: medicineQueryKeys.categories(),
    queryFn: () => medicinesApi.listCategories(),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) setData(fromMedicine(initial));
    else setData(EMPTY);
    setErrors({});
  }, [open, mode, initial]);

  const create = useMutation({
    mutationFn: (req: MedicineCreateRequest) => medicinesApi.create(req),
    onSuccess: (m) => onSaved(m),
    onError: (e) => setErrors({ _form: apiError(e).detail }),
  });
  const update = useMutation({
    mutationFn: (req: MedicineUpdateRequest) => medicinesApi.update(initial!.id, req),
    onSuccess: (m) => onSaved(m),
    onError: (e) => setErrors({ _form: apiError(e).detail }),
  });
  const busy = create.isPending || update.isPending;

  function set<K extends keyof MedicineCreateRequest>(key: K, value: MedicineCreateRequest[K]) {
    setData((d) => ({ ...d, [key]: value }));
  }

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (mode === "create" && !data.internalCode.trim())
      next.internalCode = "Mã thuốc không được để trống";
    if (!data.name.trim()) next.name = "Tên không được để trống";
    if (!data.unit.trim()) next.unit = "Đơn vị tính không được để trống";
    if (data.sellPriceVnd != null && data.sellPriceVnd < 0) next.sellPriceVnd = "≥ 0";
    if (data.costPriceVnd != null && data.costPriceVnd < 0) next.costPriceVnd = "≥ 0";
    return next;
  }

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    if (mode === "create") create.mutate(data);
    else update.mutate(stripCreateOnly(data));
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-[880px] gap-0 overflow-y-auto p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>
            {mode === "create" ? "Thêm thuốc mới" : "Sửa thuốc"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} noValidate className="px-6 py-4">
          <section className="mb-5">
            <h3 className="mb-3 text-sm font-semibold">Thông tin chung</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="m-code">Mã thuốc *</Label>
                <Input
                  id="m-code"
                  type="text"
                  value={data.internalCode}
                  onChange={(e) => set("internalCode", e.target.value)}
                  maxLength={64}
                  disabled={mode === "edit" || busy}
                  placeholder="VD: LN-PRE-109"
                  aria-invalid={Boolean(errors.internalCode)}
                />
                {errors.internalCode && (
                  <span className="text-xs text-destructive">{errors.internalCode}</span>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-name">Tên thuốc *</Label>
                <Input
                  id="m-name"
                  type="text"
                  value={data.name}
                  onChange={(e) => set("name", e.target.value)}
                  maxLength={256}
                  disabled={busy}
                  aria-invalid={Boolean(errors.name)}
                />
                {errors.name && <span className="text-xs text-destructive">{errors.name}</span>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-cls">Phân loại</Label>
                <select
                  id="m-cls"
                  className={selectClass}
                  value={data.classification}
                  onChange={(e) =>
                    set("classification", e.target.value as MedicineClassification)
                  }
                  disabled={busy}
                >
                  {(Object.keys(medicineClassificationLabel) as MedicineClassification[]).map(
                    (c) => (
                      <option key={c} value={c}>{medicineClassificationLabel[c]}</option>
                    )
                  )}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-cat">Nhóm thuốc</Label>
                <select
                  id="m-cat"
                  className={selectClass}
                  value={data.categoryId ?? ""}
                  onChange={(e) => set("categoryId", e.target.value || null)}
                  disabled={busy}
                >
                  <option value="">Chưa phân nhóm</option>
                  {categories.data?.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <section className="mb-5">
            <h3 className="mb-3 text-sm font-semibold">Dược học</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="m-ingredient">Hoạt chất</Label>
                <Input
                  id="m-ingredient"
                  type="text"
                  value={data.activeIngredient ?? ""}
                  onChange={(e) => set("activeIngredient", e.target.value || null)}
                  maxLength={256}
                  disabled={busy}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-strength">Hàm lượng</Label>
                <Input
                  id="m-strength"
                  type="text"
                  value={data.strength ?? ""}
                  onChange={(e) => set("strength", e.target.value || null)}
                  maxLength={64}
                  disabled={busy}
                  placeholder="VD: 500mg, 5mg/5ml"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-form">Dạng bào chế</Label>
                <Input
                  id="m-form"
                  type="text"
                  value={data.dosageForm ?? ""}
                  onChange={(e) => set("dosageForm", e.target.value || null)}
                  maxLength={64}
                  disabled={busy}
                  placeholder="VD: viên nén, siro"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-unit">Đơn vị tính *</Label>
                <Input
                  id="m-unit"
                  type="text"
                  value={data.unit}
                  onChange={(e) => set("unit", e.target.value)}
                  maxLength={32}
                  disabled={busy}
                  aria-invalid={Boolean(errors.unit)}
                />
                {errors.unit && <span className="text-xs text-destructive">{errors.unit}</span>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-route">Đường dùng</Label>
                <select
                  id="m-route"
                  className={selectClass}
                  value={data.routeOfAdministration ?? ""}
                  onChange={(e) => set("routeOfAdministration", e.target.value || null)}
                  disabled={busy}
                >
                  <option value="">—</option>
                  {routes.data?.map((r) => (
                    <option key={r.code} value={r.code}>{r.labelVi}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-meal">Bữa ăn (mặc định khi kê)</Label>
                <select
                  id="m-meal"
                  className={selectClass}
                  value={data.mealTiming ?? ""}
                  onChange={(e) =>
                    set("mealTiming", (e.target.value || null) as MealTiming | null)
                  }
                  disabled={busy}
                >
                  <option value="">—</option>
                  {(Object.keys(mealTimingLabel) as MealTiming[]).map((m) => (
                    <option key={m} value={m}>{mealTimingLabel[m]}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-mfr">Công ty dược</Label>
                <Input
                  id="m-mfr"
                  type="text"
                  value={data.manufacturer ?? ""}
                  onChange={(e) => set("manufacturer", e.target.value || null)}
                  maxLength={256}
                  disabled={busy}
                />
              </div>
            </div>
            <div className="mt-4 space-y-1.5">
              <Label htmlFor="m-dosage">Liều dùng (mặc định khi kê)</Label>
              <Textarea
                id="m-dosage"
                rows={2}
                value={data.dosageInstructions ?? ""}
                onChange={(e) => set("dosageInstructions", e.target.value || null)}
                maxLength={4000}
                disabled={busy}
                className="resize-y"
                placeholder="VD: Người lớn 1 viên × 3 lần / ngày"
              />
            </div>
          </section>

          <section className="mb-5">
            <h3 className="mb-3 text-sm font-semibold">Giá &amp; Thuế</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="m-sell">Giá bán (VND)</Label>
                <Input
                  id="m-sell"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1000}
                  value={data.sellPriceVnd ?? 0}
                  onChange={(e) => set("sellPriceVnd", Number(e.target.value) || 0)}
                  disabled={busy}
                  aria-invalid={Boolean(errors.sellPriceVnd)}
                />
                {errors.sellPriceVnd && (
                  <span className="text-xs text-destructive">{errors.sellPriceVnd}</span>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-cost">Giá vốn (VND)</Label>
                <Input
                  id="m-cost"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1000}
                  value={data.costPriceVnd ?? 0}
                  onChange={(e) => set("costPriceVnd", Number(e.target.value) || 0)}
                  disabled={busy}
                  aria-invalid={Boolean(errors.costPriceVnd)}
                />
                {errors.costPriceVnd && (
                  <span className="text-xs text-destructive">{errors.costPriceVnd}</span>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-vat">Loại thuế suất</Label>
                <select
                  id="m-vat"
                  className={selectClass}
                  value={data.vatCategory ?? "KCT"}
                  onChange={(e) => set("vatCategory", e.target.value as VatCategory)}
                  disabled={busy}
                >
                  {(Object.keys(vatCategoryLabel) as VatCategory[]).map((v) => (
                    <option key={v} value={v}>{vatCategoryLabel[v]}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <section className="mb-5">
            <h3 className="mb-3 text-sm font-semibold">Mã quốc gia &amp; Barcode</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="m-dqg">Mã dược quốc gia</Label>
                <Input
                  id="m-dqg"
                  type="text"
                  value={data.nationalDrugCode ?? ""}
                  onChange={(e) => set("nationalDrugCode", e.target.value || null)}
                  maxLength={64}
                  disabled={mode === "edit" || busy}
                  placeholder="VD: DQG00002084"
                />
                {mode === "edit" && (
                  <span className="text-xs text-muted-foreground">
                    Mã quốc gia chỉ chỉnh được khi import lại từ Cổng Dược Quốc Gia.
                  </span>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-bc">Barcode</Label>
                <Input
                  id="m-bc"
                  type="text"
                  value={data.barcode ?? ""}
                  onChange={(e) => set("barcode", e.target.value || null)}
                  maxLength={64}
                  disabled={mode === "edit" || busy}
                />
              </div>
            </div>
          </section>

          <section className="mb-5">
            <h3 className="mb-3 text-sm font-semibold">Hiển thị &amp; Tìm kiếm</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="m-keywords">Từ khoá tìm kiếm</Label>
                <Input
                  id="m-keywords"
                  type="text"
                  value={data.keywords ?? ""}
                  onChange={(e) => set("keywords", e.target.value || null)}
                  maxLength={512}
                  disabled={busy}
                  placeholder="VD: thucphamchucnang, 3b th plus"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-img">URL ảnh</Label>
                <Input
                  id="m-img"
                  type="url"
                  value={data.imageUrl ?? ""}
                  onChange={(e) => set("imageUrl", e.target.value || null)}
                  maxLength={512}
                  disabled={busy}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={Boolean(data.visible)}
                  onCheckedChange={(v) => set("visible", v === true)}
                  disabled={busy}
                />
                Hiển thị trong dropdown kê đơn
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={Boolean(data.controlled)}
                  onCheckedChange={(v) => set("controlled", v === true)}
                  disabled={busy}
                />
                Thuốc kiểm soát (gây nghiện / hướng thần)
              </label>
            </div>
          </section>

          {errors._form && (
            <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errors._form}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Hủy
            </Button>
            <Button type="submit" disabled={busy}>
              {busy
                ? "Đang lưu…"
                : mode === "create"
                  ? "Tạo thuốc"
                  : "Lưu thay đổi"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function stripCreateOnly(d: MedicineCreateRequest): MedicineUpdateRequest {
  // The update endpoint doesn't accept internalCode / nationalDrugCode / barcode.
  const {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    internalCode,
    nationalDrugCode,
    barcode,
    /* eslint-enable @typescript-eslint/no-unused-vars */
    ...rest
  } = d;
  return rest;
}
